// ============================================================================
// ConsultingWorkspace.jsx  (v2 — refactored)
//
// Thin state container for the Workbench. Holds workspace state, persistence,
// and event listeners. Delegates rendering to five standalone sub-components:
//   WorkbenchDashboard, WorkbenchEngagementBrief, WorkbenchFindings,
//   WorkbenchActions, WorkbenchOutputs.
//
// Each sub-tab is rendered when `initialSection` matches.
// ============================================================================

import React, { useEffect, useMemo, useState } from 'react';
import {
  getModelID, getProcessModelTitle, getAppVersion, getConsultingWorkspace, setConsultingWorkspace,
  normaliseWorkspace, normaliseFinding, normaliseAction, createDefaultWorkspace,
  loadLocalWorkspace, saveLocalWorkspace, loadWorkspaceBestEffort, saveWorkspaceBestEffort,
} from '@process-advisor/process-miner/consultant/workbenchShared';

import WorkbenchDashboard        from '@process-advisor/process-miner/consultant/WorkbenchDashboard';
import WorkbenchEngagementBrief  from '@process-advisor/process-miner/consultant/WorkbenchEngagementBrief';
import WorkbenchFindings         from '@process-advisor/process-miner/consultant/WorkbenchFindings';
import WorkbenchActions          from '@process-advisor/process-miner/consultant/WorkbenchActions';
import WorkbenchOutputs          from '@process-advisor/process-miner/consultant/WorkbenchOutputs';

// Re-export for backward compat (ImprovementBacklog, AppNav, etc.)
export { setSNApiForWorkspace } from '@process-advisor/process-miner/consultant/workbenchShared';

// ── Helpers ─────────────────────────────────────────────────────────────────
const sectionFromProp = (value) =>
  ['dashboard', 'brief', 'findings', 'actions', 'outputs'].includes(value) ? value : 'dashboard';

// ── Component ───────────────────────────────────────────────────────────────

const ConsultingWorkspace = ({ processLabel = 'Process', initialSection = 'dashboard' }) => {
  const [workspace, setWorkspace]   = useState(createDefaultWorkspace());
  const [saveState, setSaveState]   = useState('saved');
  const [activeSection, setSection] = useState(sectionFromProp(initialSection));

  const modelId    = getModelID();
  const AppVersion = getAppVersion();

  // ── Sync initialSection prop → local state ────────────────────────────
  useEffect(() => { setSection(sectionFromProp(initialSection)); }, [initialSection]);

  // ── Load workspace on mount / model change ────────────────────────────
  useEffect(() => {
    let cancelled = false;
    loadWorkspaceBestEffort({ modelId, AppVersion }).then((loaded) => {
      if (!cancelled) setWorkspace(loaded);
    });
    return () => { cancelled = true; };
  }, [modelId, AppVersion]);

  // ── Listen for external workspace updates ─────────────────────────────
  useEffect(() => {
    const handler = (event) => {
      const next = event?.detail?.workspace ? normaliseWorkspace(event.detail.workspace) : null;
      if (next) {
        setConsultingWorkspace(next);
        saveLocalWorkspace(modelId, next);
        setWorkspace(next);
        return;
      }
      loadWorkspaceBestEffort({ modelId, AppVersion }).then(setWorkspace);
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('metricus:consulting-workspace-updated', handler);
      window.addEventListener('metricus:workspace-updated', handler);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('metricus:consulting-workspace-updated', handler);
        window.removeEventListener('metricus:workspace-updated', handler);
      }
    };
  }, [modelId, AppVersion]);

  // ── Broadcast save state for AppNav "Unsaved changes" indicator ───────
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('metricus:workbench-save-state', { detail: { saveState } }));
  }, [saveState]);

  // ── Listen for AppNav "Save Workbench" button ─────────────────────────
  useEffect(() => {
    const handler = () => { saveNow(); };
    if (typeof window !== 'undefined') window.addEventListener('metricus:save-workbench', handler);
    return () => { if (typeof window !== 'undefined') window.removeEventListener('metricus:save-workbench', handler); };
  }, [workspace, modelId, AppVersion]);

  // ── Mutation helpers ──────────────────────────────────────────────────
  const updateWorkspace = (patch) => {
    setWorkspace(prev => {
      const next = { ...prev, ...patch, updatedAt: new Date().toISOString() };
      setConsultingWorkspace(next);
      saveLocalWorkspace(modelId, next);
      setSaveState('dirty');
      return next;
    });
  };

  const saveNow = async () => {
    setSaveState('saving');
    const next = { ...workspace, updatedAt: new Date().toISOString() };
    setWorkspace(next);
    const result = await saveWorkspaceBestEffort({ modelId, workspace: next, AppVersion });
    setSaveState(result.savedToBackend ? 'saved' : 'local');
  };

  // ── Finding mutations ─────────────────────────────────────────────────
  const addFinding = () => {
    const finding = normaliseFinding({ title: 'New finding', sourceArea: 'Assessment' });
    updateWorkspace({ findings: [finding, ...workspace.findings] });
  };

  const updateFinding = (id, patch) => {
    updateWorkspace({
      findings: workspace.findings.map(f =>
        f.id === id ? normaliseFinding({ ...f, ...patch, updatedAt: new Date().toISOString() }) : f
      ),
    });
  };

  const removeFinding = (id) => {
    updateWorkspace({
      findings: workspace.findings.filter(f => f.id !== id),
      actions:  workspace.actions.map(a => a.findingId === id ? { ...a, findingId: '' } : a),
    });
  };

  const convertFinding = (finding) => {
    const title = finding.action || finding.recommendation || finding.title || finding.finding || 'Action from finding';
    const action = normaliseAction({
      findingId: finding.id,
      title,
      priority: finding.severity,
      expectedValue: finding.valuePotential,
      effort: finding.effort,
      evidenceSummary: (finding.evidence || []).join('\n'),
      implementationSteps: finding.action || finding.recommendation || '',
    });
    updateWorkspace({
      actions:  [action, ...workspace.actions],
      findings: workspace.findings.map(f =>
        f.id === finding.id
          ? { ...f, status: 'Converted to Action', updatedAt: new Date().toISOString() }
          : f
      ),
    });
    setSection('actions');
  };

  // ── Action mutations ──────────────────────────────────────────────────
  const addAction = () => {
    const action = normaliseAction({ title: 'New action' });
    updateWorkspace({ actions: [action, ...workspace.actions] });
  };

  const updateAction = (id, patch) => {
    updateWorkspace({
      actions: workspace.actions.map(a =>
        a.id === id ? normaliseAction({ ...a, ...patch, updatedAt: new Date().toISOString() }) : a
      ),
    });
  };

  const removeAction = (id) => {
    updateWorkspace({ actions: workspace.actions.filter(a => a.id !== id) });
  };

  // ── Render ────────────────────────────────────────────────────────────
  switch (activeSection) {
    case 'brief':
      return (
        <WorkbenchEngagementBrief
          workspace={workspace}
          onUpdate={updateWorkspace}
          processLabel={processLabel}
        />
      );

    case 'findings':
      return (
        <WorkbenchFindings
          workspace={workspace}
          onUpdateFinding={updateFinding}
          onRemoveFinding={removeFinding}
          onAddFinding={addFinding}
          onConvertFinding={convertFinding}
        />
      );

    case 'actions':
      return (
        <WorkbenchActions
          workspace={workspace}
          onUpdateAction={updateAction}
          onRemoveAction={removeAction}
          onAddAction={addAction}
        />
      );

    case 'outputs':
      return (
        <WorkbenchOutputs
          workspace={workspace}
          onUpdate={updateWorkspace}
        />
      );

    case 'dashboard':
    default:
      return (
        <WorkbenchDashboard
          workspace={workspace}
        />
      );
  }
};

export default ConsultingWorkspace;
